/**
 * Simple sample-based instrument player.
 * Loads a set of one-shot samples keyed by MIDI, then plays any target MIDI
 * by picking the nearest sample and pitch-shifting via playbackRate.
 */

const LETTER_SEMITONES: Record<string, number> = {
  C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11,
};

/** "A4" -> 69, "C5" -> 72, "F#4" -> 66, "As4" -> 70, "Cs5" -> 73 */
export function noteNameToMidi(name: string): number | null {
  const m = name.match(/^([A-G])([#bs]?)(-?\d+)$/);
  if (!m) return null;
  const [, letter, acc, octStr] = m;
  const base = LETTER_SEMITONES[letter];
  if (base === undefined) return null;
  const accSemis = acc === '#' || acc === 's' ? 1 : acc === 'b' ? -1 : 0;
  const oct = parseInt(octStr, 10);
  return (oct + 1) * 12 + base + accSemis;
}

export interface SampleDef {
  midi: number;
  url: string;
}

/** Available flute samples in /public/samples/flute (Philharmonia). */
export function buildFluteSampleList(): SampleDef[] {
  // All files follow the pattern flute_<note>_1_forte_normal.mp3
  const notes = [
    'C4','Cs4','D4','Ds4','E4','F4','Fs4','G4','Gs4','A4','As4','B4',
    'C5','Cs5','D5','Ds5','E5','F5','Fs5','G5','Gs5','A5','As5','B5',
    'C6','Cs6','D6','Ds6','E6','F6','Fs6','G6','Gs6','A6','As6','B6',
    'C7','Cs7','D7','Ds7','E7','F7',
  ];
  return notes.flatMap(n => {
    const midi = noteNameToMidi(n);
    if (midi === null) return [];
    return [{ midi, url: `/samples/flute/flute_${n}_1_forte_normal.mp3` }];
  });
}

/** Available violin samples in /public/samples/violin (Philharmonia). */
export function buildViolinSampleList(): SampleDef[] {
  // Each entry: [noteName, suffix] where suffix = dynamic + '_' + style
  const files: [string, string][] = [
    // Natural notes
    ['A3', 'forte_arco-normal'],
    ['A4', 'fortissimo_arco-normal'], ['A5', 'forte_arco-normal'], ['A6', 'forte_arco-normal'],
    ['B3', 'forte_arco-normal'],
    ['B4', 'forte_arco-normal'],      ['B5', 'forte_arco-normal'], ['B6', 'forte_arco-normal'],
    ['C4', 'forte_arco-normal'],
    ['C5', 'forte_con-sord'],         ['C6', 'forte_arco-normal'], ['C7', 'forte_arco-normal'],
    ['D4', 'forte_arco-normal'],
    ['D5', 'forte_arco-normal'],      ['D6', 'forte_arco-normal'], ['D7', 'forte_arco-normal'],
    ['E4', 'forte_con-sord'],
    ['E5', 'forte_arco-normal'],      ['E6', 'forte_arco-normal'], ['E7', 'forte_arco-normal'],
    ['F4', 'forte_arco-normal'],
    ['F5', 'forte_arco-normal'],      ['F6', 'forte_arco-normal'], ['F7', 'forte_arco-normal'],
    ['G3', 'forte_arco-normal'],
    ['G4', 'forte_arco-normal'],      ['G5', 'forte_arco-normal'], ['G6', 'forte_arco-normal'],
    ['G7', 'fortissimo_arco-normal'],
    // Sharp notes
    ['As3', 'forte_con-sord'],
    ['As4', 'forte_arco-normal'],     ['As5', 'forte_arco-normal'], ['As6', 'forte_arco-normal'],
    ['Cs4', 'forte_con-sord'],
    ['Cs5', 'forte_arco-normal'],     ['Cs6', 'forte_arco-normal'], ['Cs7', 'forte_arco-normal'],
    ['Ds4', 'forte_arco-normal'],
    ['Ds5', 'forte_arco-normal'],     ['Ds6', 'forte_arco-normal'], ['Ds7', 'forte_arco-normal'],
    ['Fs4', 'forte_arco-normal'],
    ['Fs5', 'forte_arco-normal'],     ['Fs6', 'forte_arco-normal'], ['Fs7', 'forte_arco-normal'],
    ['Gs3', 'forte_arco-normal'],
    ['Gs4', 'forte_arco-normal'],     ['Gs5', 'forte_arco-normal'], ['Gs6', 'forte_arco-normal'],
  ];
  return files.flatMap(([note, suffix]) => {
    const midi = noteNameToMidi(note);
    if (midi === null) return [];
    return [{ midi, url: `/samples/violin/violin_${note}_1_${suffix}.mp3` }];
  });
}

/** Build a sample list for instruments where every file shares the same suffix. */
function buildUniformSampleList(instrument: string, notes: string[], suffix: string): SampleDef[] {
  return notes.flatMap(n => {
    const midi = noteNameToMidi(n);
    if (midi === null) return [];
    return [{ midi, url: `/samples/${instrument}/${instrument}_${n}_${suffix}.mp3` }];
  });
}

export function buildCelloSampleList(): SampleDef[] {
  const notes = [
    'C2','Cs2','D2','Ds2','E2','F2','Fs2','G2','Gs2','A2','As2','B2',
    'C3','Cs3','D3','Ds3','E3','F3','Fs3','G3','Gs3','A3','As3',
    'C4','Cs4','D4','Ds4','E4','F4','Fs4','G4','Gs4','A4','As4','B4',
    'C5','Cs5','D5','Ds5','E5','F5','Fs5','G5','Gs5','A5','As5','B5',
    'C6',
  ];
  return buildUniformSampleList('cello', notes, '1_forte_arco-normal');
}

export function buildClarinetSampleList(): SampleDef[] {
  const notes = [
    'D3','Ds3','E3','F3','Fs3','G3','Gs3','A3','As3','B3',
    'C4','Cs4','D4','Ds4','E4','F4','Fs4','G4','Gs4','A4','As4','B4',
    'C5','Cs5','D5','Ds5','E5','F5','Fs5','G5','Gs5','A5','As5','B5',
    'C6','Cs6','D6','Ds6','E6','F6','Fs6','G6','Gs6','A6','As6','B6',
    'C7',
  ];
  return buildUniformSampleList('clarinet', notes, '1_forte_normal');
}

export function buildGuitarSampleList(): SampleDef[] {
  const notes = [
    'E2','F2','Fs2','G2','Gs2','A2','As2','B2',
    'C3','Cs3','D3','Ds3','E3','F3','Fs3','G3','Gs3','A3','As3','B3',
    'C4','Cs4','D4','Ds4','E4','F4','Fs4','G4','Gs4','A4','As4','B4',
    'D5','Ds5','E5','G5','Gs5',
  ];
  return buildUniformSampleList('guitar', notes, 'very-long_forte_normal');
}

export function buildOboeSampleList(): SampleDef[] {
  const notes = [
    'As3','B3',
    'C4','Cs4','D4','Ds4','E4','F4','Fs4','G4','Gs4','A4','As4','B4',
    'C5','Cs5','D5','Ds5','E5','F5','Fs5','G5','Gs5','A5','As5','B5',
    'C6','Cs6','D6','Ds6','E6','F6','Fs6','G6','Gs6',
  ];
  return buildUniformSampleList('oboe', notes, '1_forte_normal');
}

export function buildSaxophoneSampleList(): SampleDef[] {
  // D3 is fortissimo; everything else is forte
  const files: [string, string][] = [
    ['D3','1_fortissimo_normal'],
    ...([
      'Ds3','E3','F3','Fs3','G3','Gs3','A3','As3','B3',
      'C4','Cs4','D4','Ds4','E4','F4','Fs4','G4','Gs4','A4','As4','B4',
      'C5','Cs5','D5','Ds5','E5','F5','Fs5','G5','Gs5','A5','As5','B5',
      'C6','Cs6','D6','Ds6','E6','F6',
    ] as string[]).map(n => [n, '1_forte_normal'] as [string, string]),
  ];
  return files.flatMap(([note, suffix]) => {
    const midi = noteNameToMidi(note);
    if (midi === null) return [];
    return [{ midi, url: `/samples/saxophone/saxophone_${note}_${suffix}.mp3` }];
  });
}

export function buildTrumpetSampleList(): SampleDef[] {
  // suffix format: <variant>_pianissimo_normal
  const files: [string, string][] = [
    ['E2','long'], ['F2','long'], ['Fs2','long'], ['G2','long'], ['Gs2','long'],
    ['A2','long'], ['As2','long'],
    ['Cs3','long'],
    ['E3','1'], ['F3','1'], ['G3','1'], ['Gs3','1'], ['A3','1'], ['As3','1'], ['B3','1'],
    ['C4','1'], ['Cs4','1'], ['D4','1'], ['Ds4','1'], ['E4','1'], ['F4','1'],
    ['Fs4','1'], ['G4','1'], ['Gs4','1'], ['A4','1'], ['As4','1'], ['B4','1'],
    ['C5','1'], ['Cs5','1'], ['D5','1'], ['Ds5','1'], ['E5','1'], ['F5','1'],
    ['G5','1'], ['Gs5','1'], ['As5','1'], ['B5','15'],
    ['C6','1'], ['D6','1'],
  ];
  return files.flatMap(([note, variant]) => {
    const midi = noteNameToMidi(note);
    if (midi === null) return [];
    return [{ midi, url: `/samples/trumpet/trumpet_${note}_${variant}_pianissimo_normal.mp3` }];
  });
}

/** Return the sample list for a given instrument key. Falls back to flute. */
export function buildSampleList(instrument: string): SampleDef[] {
  switch (instrument) {
    case 'violin':    return buildViolinSampleList();
    case 'cello':     return buildCelloSampleList();
    case 'clarinet':  return buildClarinetSampleList();
    case 'guitar':    return buildGuitarSampleList();
    case 'oboe':      return buildOboeSampleList();
    case 'saxophone': return buildSaxophoneSampleList();
    case 'trumpet':   return buildTrumpetSampleList();
    default:          return buildFluteSampleList();
  }
}

interface LoadedSample {
  buffer: AudioBuffer;
  /** Offset (seconds) into the buffer where the actual attack begins. */
  attackOffsetSec: number;
}

/**
 * Scan a buffer for the first sample where any channel rises above `threshold`,
 * then back off slightly so we keep a tiny pre-transient for a natural attack.
 */
function detectAttackOffset(buffer: AudioBuffer, threshold = 0.02, preRollMs = 5): number {
  const sampleRate = buffer.sampleRate;
  const channels = buffer.numberOfChannels;
  const length = buffer.length;
  for (let i = 0; i < length; i++) {
    for (let c = 0; c < channels; c++) {
      const v = buffer.getChannelData(c)[i];
      if (Math.abs(v) > threshold) {
        const offset = Math.max(0, i - Math.round((preRollMs / 1000) * sampleRate));
        return offset / sampleRate;
      }
    }
  }
  return 0;
}

export class InstrumentSampler {
  private ctx: AudioContext | null = null;
  private buffers = new Map<number, LoadedSample>();
  private currentSource: AudioBufferSourceNode | null = null;
  private currentGain: GainNode | null = null;
  private loaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(private samples: SampleDef[]) {}

  /** Lazily create AudioContext (needs user gesture on some browsers). */
  private ensureCtx(): AudioContext {
    if (!this.ctx) {
      const C =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      this.ctx = new C();
    }
    return this.ctx!;
  }

  async load(): Promise<void> {
    if (this.loaded) return;
    if (this.loadingPromise) return this.loadingPromise;
    const ctx = this.ensureCtx();
    this.loadingPromise = (async () => {
      await Promise.all(
        this.samples.map(async (s) => {
          try {
            const res = await fetch(s.url);
            if (!res.ok) return;
            const arr = await res.arrayBuffer();
            const buf = await ctx.decodeAudioData(arr);
            const attackOffsetSec = detectAttackOffset(buf);
            this.buffers.set(s.midi, { buffer: buf, attackOffsetSec });
          } catch {
            /* ignore missing sample */
          }
        })
      );
      this.loaded = true;
    })();
    await this.loadingPromise;
  }

  private nearestMidi(target: number): number | null {
    let best: number | null = null;
    let bestDist = Infinity;
    for (const midi of this.buffers.keys()) {
      const d = Math.abs(midi - target);
      if (d < bestDist) {
        bestDist = d;
        best = midi;
      }
    }
    return best;
  }

  /** Start a note now. Automatically stops previous note (monophonic). */
  playNote(midi: number, durationMs: number, velocity = 0.9): void {
    if (!this.ctx) return;
    const nearest = this.nearestMidi(midi);
    if (nearest === null) return;
    const sample = this.buffers.get(nearest);
    if (!sample) return;

    this.stop();

    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = sample.buffer;
    src.playbackRate.value = Math.pow(2, (midi - nearest) / 12);

    const gain = ctx.createGain();
    const now = ctx.currentTime;
    const durSec = Math.max(0.05, durationMs / 1000);
    // Very short fade-in so we don't click, but fast enough to feel "on time".
    const attackSec = 0.005;
    // Short release so consecutive notes don't bleed into each other.
    const releaseSec = Math.min(0.04, durSec * 0.3);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(velocity, now + attackSec);
    gain.gain.setValueAtTime(velocity, now + Math.max(attackSec, durSec - releaseSec));
    gain.gain.linearRampToValueAtTime(0, now + durSec);

    src.connect(gain).connect(ctx.destination);
    // Start past the leading silence / breath noise so the perceived attack
    // is aligned with the beat.
    src.start(now, sample.attackOffsetSec);
    src.stop(now + durSec + 0.02);

    this.currentSource = src;
    this.currentGain = gain;
  }

  /** Stop any currently playing sample with a short fade. */
  stop(): void {
    if (!this.ctx || !this.currentSource || !this.currentGain) {
      this.currentSource = null;
      this.currentGain = null;
      return;
    }
    const ctx = this.ctx;
    const g = this.currentGain;
    const s = this.currentSource;
    const now = ctx.currentTime;
    try {
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.linearRampToValueAtTime(0, now + 0.03);
      s.stop(now + 0.04);
    } catch {
      /* already stopped */
    }
    this.currentSource = null;
    this.currentGain = null;
  }

  /** Resume the underlying context (needed after user gesture on some browsers). */
  async resume(): Promise<void> {
    const ctx = this.ensureCtx();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
  }
}
